import { useEffect } from 'react';
import { Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;
const NOTIFICATION_ID = 'app-update-available';

// The hashed path of the main bundle this page actually loaded (e.g.
// /static/js/main.abc123.js) — the client's version fingerprint. Reading it
// from the DOM (rather than a fetch at startup) means there's no race with a
// deploy that lands between page load and the first check.
function getLoadedBundlePath(): string | null {
  const script = document.querySelector<HTMLScriptElement>(
    'script[src*="/static/js/main."]'
  );
  if (!script) return null;
  return new URL(script.src, window.location.origin).pathname;
}

// asset-manifest.json is regenerated on every build, so the main.js entry it
// points at identifies the currently deployed version.
async function fetchDeployedBundlePath(): Promise<string | null> {
  const response = await fetch('/asset-manifest.json', { cache: 'no-store' });
  if (!response.ok) return null;
  const manifest = await response.json();
  const path = manifest?.files?.['main.js'];
  return typeof path === 'string' ? path : null;
}

function showUpdateNotification() {
  notifications.show({
    id: NOTIFICATION_ID,
    position: 'bottom-left',
    autoClose: false,
    withCloseButton: false,
    // Rounding, border and shadow now come from the app-wide Notification
    // defaults (App.tsx + index.css) that this toast's styling seeded.
    message: (
      <>
        <Text size="sm" fw={500}>
          A new version of Meals You Love is available
        </Text>
        <Group gap="xs" mt="sm">
          <Button size="xs" onClick={() => window.location.reload()}>
            Update now
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => notifications.hide(NOTIFICATION_ID)}
          >
            Later
          </Button>
        </Group>
      </>
    ),
  });
}

// Polls the deployed asset manifest and shows a one-time "update available"
// toast when it no longer matches the bundle this page loaded. Checks run on
// an interval and when the tab regains focus (the case that actually matters
// for stale, backgrounded tabs). Renders nothing.
export function AppUpdateNotifier() {
  useEffect(() => {
    // The dev server serves an unhashed bundle, so there's nothing to compare.
    if (process.env.NODE_ENV !== 'production') return;

    const loadedBundle = getLoadedBundlePath();
    if (!loadedBundle) return;

    let notified = false;
    let lastCheckedAt = 0;

    const check = async () => {
      if (notified || document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastCheckedAt < MIN_CHECK_GAP_MS) return;
      lastCheckedAt = now;
      try {
        const deployedBundle = await fetchDeployedBundlePath();
        if (deployedBundle && deployedBundle !== loadedBundle) {
          notified = true;
          showUpdateNotification();
        }
      } catch {
        // Offline or transient failure — the next check will retry.
      }
    };

    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
}
